import { BankAccountSchema, EvidenceIdSchema, EvidenceKindSchema, MoneySchema, TransactionDetailSchema, } from '@pf/contracts';
import { z } from 'zod';
/**
 * The deposit flow — the part of the profile that is mostly *pass-through*.
 *
 * Every response on this path is a `@pf/contracts` schema, named unchanged in the endpoint
 * manifest (`endpoints.ts`): `DepositOptionsResponse`, `ReserveDepositOptionResponse`,
 * `TransactionDetail`, `MarkPaidResponse`, `SubmitEvidenceResponse`,
 * `CreateEvidenceUploadResponse`, `ReleaseTransactionResponse`. The profile deliberately does not
 * restate them. A BFF that reshapes PaymentFlow's transaction view is free to, but then it owns a
 * mapping forever, and the reference UI already renders PF's shape — including the verification
 * check matrix and the event timeline, which are the two things that make the deposit screen
 * worth looking at.
 *
 * The **requests** are where the profile does diverge, and always in the same direction: fields
 * the browser must not be allowed to choose are removed. Compare
 * `ReserveDepositOptionRequestSchema` in `@pf/contracts` — it carries `client_reference` and a
 * `depositor` — with {@link ReserveDepositRequestSchema} below, which carries neither. Both are
 * the BFF's to supply: `depositor.account_ref` comes from the session (a browser that could name
 * the depositor could deposit as somebody else), and `client_reference` is what PaymentFlow
 * idempotency-keys the reservation on (a browser that could choose it could replay or collide
 * with another customer's claim).
 */
/**
 * `POST /bff/deposit-options/reserve` — claim exactly one withdrawal at this amount.
 *
 * The amount and nothing else. `409 OUT_OF_STOCK` when the bucket is empty, which is a normal
 * outcome under contention rather than an error: someone else claimed it first.
 */
export const ReserveDepositRequestSchema = z
    .object({
    amount: MoneySchema,
})
    .strict();
/** `POST /bff/deposits/{id}/mark-paid` — no body. The transfer has been made. */
export const MarkDepositPaidRequestSchema = z.object({}).strict();
/** `POST /bff/deposits/{id}/release` — no body. Give the slot back before paying. */
export const ReleaseDepositRequestSchema = z.object({}).strict();
/**
 * `POST /bff/evidence` — mint a short-TTL pre-signed upload URL.
 *
 * Step one of the two-step upload (SAD §8): the browser PUTs the slip image straight to object
 * storage and then submits only the reference, so the image never passes through the BFF or the
 * API. A BFF that proxies the bytes instead is making itself a bottleneck on the one payload in
 * the system that is measured in megabytes.
 */
export const CreateEvidenceUploadRequestSchema = z
    .object({
    kind: EvidenceKindSchema,
    content_type: z.enum(['image/jpeg', 'image/png', 'application/pdf']).optional(),
})
    .strict();
/**
 * `POST /bff/deposits/{id}/evidence` — step two: submit the slip and trigger verification.
 *
 * At least one of `qr_payload` (preferred — SAD §6 reads the QR directly and needs no image) or
 * `evidence_id` (from step one) must be present.
 */
export const SubmitDepositEvidenceRequestSchema = z
    .object({
    kind: EvidenceKindSchema,
    qr_payload: z.string().min(1).optional(),
    evidence_id: EvidenceIdSchema.optional(),
})
    .strict()
    .refine((body) => body.qr_payload !== undefined || body.evidence_id !== undefined, {
    message: 'at least one of qr_payload or evidence_id is required',
    path: ['qr_payload'],
});
/**
 * `GET /bff/deposits/active` — the customer's in-flight deposit, if they have one.
 *
 * ## Why this endpoint has to exist
 *
 * Reserving is an *exclusive claim*: it takes one withdrawal out of the order book and holds it
 * for this customer alone until they pay, give it up, or the TTL lapses. Two things follow, and
 * together they made the reservation unrecoverable:
 *
 *  1. The claimed amount vanishes from `GET /deposit-options` — correctly, since nobody else may
 *     take it. To the customer who claimed it, the book simply looks empty.
 *  2. The withdrawer's bank details are disclosed *once*, in the 201 from the reserve call.
 *     `TransactionDetail` never echoes them, by design (SAD §5: no bank details until reserved).
 *
 * So a UI that held the reserve response in memory lost the whole reservation on a refresh: the
 * claim was still live server-side for the rest of its TTL, the money was still owed, and the
 * screen had no way back to it. That is what this endpoint fixes.
 *
 * Re-disclosing `pay_to` here is not a widening of SAD §5. The rule is "no bank details **until**
 * reserved"; this customer holds the reservation, has already been shown the account, and cannot
 * pay without it. The BFF answers from its own record of the reservation it made.
 */
export const ActiveDepositSchema = z
    .object({
    /** The authoritative transaction, read through to PaymentFlow. */
    transaction: TransactionDetailSchema,
    /**
     * Where to pay. `null` when the BFF has no record of the payee — an older reservation, or a
     * deposit that reached this state by some path other than this BFF's own reserve call.
     */
    pay_to: BankAccountSchema.nullable(),
    /**
     * Where the customer must pay *from* — PaymentFlow's `pay_from` echo of the reservation's
     * `payer_account`, masked (see `ReserveDepositOptionResponseSchema.pay_from`). `null` when
     * the reservation carried no `payer_account` (the sender check is not applicable) or when
     * the BFF has no record of it — the same two shades of "none" as `pay_to`.
     */
    pay_from: BankAccountSchema.nullable(),
})
    .strict();
/**
 * `200` response for `GET /bff/deposits/active`.
 *
 * Wrapped rather than returning a bare nullable body, so "no deposit in flight" is an ordinary
 * 200 a UI can render, not a 404 it has to tell apart from a real one.
 */
export const ActiveDepositResponseSchema = z
    .object({ active: ActiveDepositSchema.nullable() })
    .strict();
//# sourceMappingURL=deposit.js.map